import type { CallNotificationId, IsoDateTimeString, PhoneNumber, TimeSpanString } from './common';
import type { Position } from './incident';

export const CallNotificationSource = {
  AppleEED: 'AppleEED',
  AML: 'AML',
  SIP: 'SIP',
} as const;
export type CallNotificationSource = (typeof CallNotificationSource)[keyof typeof CallNotificationSource];

export const CallTranscriptMessageSource = {
  Caller: 'Caller',
  Callee: 'Callee',
} as const;
export type CallTranscriptMessageSource = (typeof CallTranscriptMessageSource)[keyof typeof CallTranscriptMessageSource];

export const CallTranscriptMessageType = {
  InterimTranscript: 'InterimTranscript',
  FinalTranscript: 'FinalTranscript',
  EnhancedTranscript: 'EnhancedTranscript',
} as const;
export type CallTranscriptMessageType = (typeof CallTranscriptMessageType)[keyof typeof CallTranscriptMessageType];

export const CallTranscriptEventCategory = {
  Unknown: 'Unknown',
  AddressVerified: 'AddressVerified',
  CallbackNumberVerified: 'CallbackNumberVerified',
  ChiefComplaintIdentified: 'ChiefComplaintIdentified',
  PatientAgeDetermined: 'PatientAgeDetermined',
  PatientGenderDetermined: 'PatientGenderDetermined',
  ConsciousnessLevelAlert: 'ConsciousnessLevelAlert',
  ConsciousnessLevelVerbal: 'ConsciousnessLevelVerbal',
  ConsciousnessLevelPain: 'ConsciousnessLevelPain',
  ConsciousnessLevelUnresponsive: 'ConsciousnessLevelUnresponsive',
  BreathingStatusNormal: 'BreathingStatusNormal',
  BreathingStatusAbsent: 'BreathingStatusAbsent',
  AgonalBreathingDetected: 'AgonalBreathingDetected',
  BreathingRestored: 'BreathingRestored',
  CardiacArrestRecognized: 'CardiacArrestRecognized',
  HandsOnlyCprInstructed: 'HandsOnlyCprInstructed',
  SternumLocated: 'SternumLocated',
  ChestCompressionsStarted: 'ChestCompressionsStarted',
  ChestCompressionsPaused: 'ChestCompressionsPaused',
  ChestCompressionsResumed: 'ChestCompressionsResumed',
  RescueBreathsStarted: 'RescueBreathsStarted',
  RescueBreathsDelivered: 'RescueBreathsDelivered',
  CompressionFrequencyMonitored: 'CompressionFrequencyMonitored',
  AedAvailableCheckCompleted: 'AedAvailableCheckCompleted',
  AedPadsApplied: 'AedPadsApplied',
  AedShockDelivered: 'AedShockDelivered',
  InfantCprInitiated: 'InfantCprInitiated',
  ChildCprInitiated: 'ChildCprInitiated',
  ChokingInterventionStarted: 'ChokingInterventionStarted',
  HemorrhagePresenceConfirmed: 'HemorrhagePresenceConfirmed',
  HemorrhageControlInitiated: 'HemorrhageControlInitiated',
  AirwayCleared: 'AirwayCleared',
  TurnedToSide: 'TurnedToSide',
  VomitingReported: 'VomitingReported',
  ShockPositioningInstructed: 'ShockPositioningInstructed',
  ChildbirthSupportStarted: 'ChildbirthSupportStarted',
  BabyCrowningObserved: 'BabyCrowningObserved',
  PostDeliveryCareStarted: 'PostDeliveryCareStarted',
  UmbilicalCordManagementInstructed: 'UmbilicalCordManagementInstructed',
  SceneSafetyConfirmed: 'SceneSafetyConfirmed',
  WeaponsAssailantCheckCompleted: 'WeaponsAssailantCheckCompleted',
  HazmatDangerCheckCompleted: 'HazmatDangerCheckCompleted',
  MechanismOfInjuryIdentified: 'MechanismOfInjuryIdentified',
  PetsSecured: 'PetsSecured',
  EntryAccessSecured: 'EntryAccessSecured',
  DispatchInitiated: 'DispatchInitiated',
  ResponseModeHot: 'ResponseModeHot',
  ResponseModeCold: 'ResponseModeCold',
  ResourceLevelSetBls: 'ResourceLevelSetBls',
  ResourceLevelSetAls: 'ResourceLevelSetAls',
  HysteriaThresholdReached: 'HysteriaThresholdReached',
  RepetitivePersistenceInitiated: 'RepetitivePersistenceInitiated',
  ItsNotWorkingSyndromeDetected: 'ItsNotWorkingSyndromeDetected',
  TelescopingOfTimeSyndromeDetected: 'TelescopingOfTimeSyndromeDetected',
  SecondaryPatientIdentified: 'SecondaryPatientIdentified',
  ObjectionToPersistentQuestioning: 'ObjectionToPersistentQuestioning',
  MedicsArrived: 'MedicsArrived',
} as const;
export type CallTranscriptEventCategory = (typeof CallTranscriptEventCategory)[keyof typeof CallTranscriptEventCategory];

export type CallTranscriptWordTimestamp = {
  word: string;
  start: TimeSpanString;
  end: TimeSpanString;
};

export type CallTranscriptMessage = {
  startOffset: TimeSpanString;
  endOffset: TimeSpanString;
  source: CallTranscriptMessageSource;
  originalContent: string;
  content: string;
  type: CallTranscriptMessageType;
  recognizedWords: CallTranscriptWordTimestamp[] | null;
  messageId: string | null;
  parentMessageId: string | null;
};

export type CallTranscriptEvent = {
  offset: TimeSpanString;
  category: CallTranscriptEventCategory;
  title: string;
  description: string;
};

export type CallTranscript = {
  messages: CallTranscriptMessage[];
  detectedEvents: CallTranscriptEvent[];
  summary: string | null;
};

export type CallNotificationItem = {
  id: CallNotificationId;
  callId: PhoneNumber;
  createdTime: IsoDateTimeString;
  startTimeUtc: IsoDateTimeString;
  endTimeUtc: IsoDateTimeString | null;
  lastPosition: Position | null;
  source: CallNotificationSource;
  callTranscript: CallTranscript | null;
};
